<script setup>
import { useGlobalStore } from '@stores/global'
import Heading from '@sections/home/heading.vue'
import Button from '@components/global/button.vue'
import { ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'

const breakpoints = ref({
  0: {
    slidesPerView: 2,
  },
  500: {
    slidesPerView: 3,
  },
})

const global = useGlobalStore()
</script>

<template>
  <section class="container flex flex-col overflow-hidden">
    <Heading>
      <template #title>
        <span class="font-semibold leading-[1.1]">about</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="154"
          height="34"
          viewBox="0 0 154 34"
          fill="none"
          class="text-sf-purple-base"
        >
          <path
            d="M0.231934 0.671841L15.7839 33.5038H17.7519L33.3999 0.671841H26.5839L23.7519 6.91184H9.73593L6.90393 0.671841H0.231934ZM12.2319 12.4798H21.2559L16.7439 22.4638L12.2319 12.4798Z"
            fill="currentColor"
          />
          <path
            d="M35.1846 0.671841V33.1678H48.0006C55.9686 33.1678 58.6086 27.9838 58.6086 24.2878C58.6086 21.3598 57.3606 19.1038 55.4406 17.7598C58.1286 16.1758 59.5686 13.4398 59.5686 10.2238C59.5686 5.80784 56.3046 0.671841 48.9606 0.671841H45.6966L44.0166 6.43184H48.4326C51.4566 6.43184 53.2806 7.96784 53.2806 10.4158C53.2806 12.9598 51.4566 14.3998 48.4326 14.3998H41.4246V0.671841H35.1846ZM41.4246 19.9198H47.8566C50.6886 19.9198 52.4166 21.3118 52.4166 23.6158C52.4166 25.9678 50.6886 27.4078 47.8566 27.4078H41.4246V19.9198Z"
            fill="currentColor"
          />
          <path
            d="M79.2902 -0.000160217C69.3542 -0.000160217 62.2502 7.29584 62.2502 16.9438C62.2502 26.5438 69.3542 33.8398 79.2902 33.8398C89.0822 33.8398 96.2822 26.5918 96.2822 16.9438C96.2822 7.29584 89.1302 -0.000160217 79.2902 -0.000160217ZM68.6342 16.9438C68.6342 10.4158 73.2422 5.85584 79.2902 5.85584C85.2422 5.85584 89.8982 10.4158 89.8982 16.9438C89.8982 23.4238 85.2422 27.9838 79.2902 27.9838C73.2422 27.9838 68.6342 23.4238 68.6342 16.9438Z"
            fill="currentColor"
          />
          <path
            d="M115.291 0.527843H113.419L98.0589 33.1678H104.875L114.427 12.2398L123.979 33.1678H130.651L115.291 0.527843Z"
            fill="currentColor"
          />
          <path
            d="M132.356 0.671841V33.1678H152.804V27.3118H138.596V19.9198H152.036V14.3038H138.596V6.52784H153.668L152.036 0.671841H132.356Z"
            fill="currentColor"
          />
        </svg>
      </template>
      <template #text> Excepteur do minim eu ad eu anim quis qui elit non ullamco. </template>
      <template #button>
        <Button class="btn-outline" :to="{ name: 'about' }"> go to about </Button>
      </template>
    </Heading>

    <div class="relative">
      <video
        src="https://hennep.s3.amazonaws.com/above/images/home/about/video.mp4"
        autoplay
        muted
        loop
        playsinline
        disablePictureInPicture
        webkit-playsinline
        class="absolute-x-center video-no-controls -top-[20%] h-[900px] w-full mix-blend-screen"
      />
      <img
        src="https://hennep.s3.amazonaws.com/above/images/home/about/ufo.svg"
        alt=""
        class="absolute-x-center -top-[30%] h-full w-[80px]"
      />
      <div class="relative flex w-full flex-col items-center gap-16 self-center p-16 md:p-32">
        <h1 class="flex flex-col text-center font-heading font-medium uppercase text-text-tertiary">
          not your <span> average earth</span> experience.
        </h1>
        <span class="max-w-[400px] text-center font-body text-body-sm uppercase text-purple-300">
          Excepteur do minim eu ad eu anim quis qui elit non ullamco.
          <br />
          <br />
          Consectetur proident anim id pariatur minim laboris aute irure dolor enim sunt nostrud.</span
        >
        <div class="h-[70px] w-[300px] overflow-hidden md:block">
          <Swiper :breakpoints="breakpoints" class="h-full">
            <SwiperSlide
              v-for="social in global.socials"
              :key="social.avatar_url"
              class="flex items-center justify-center"
            >
              <Button
                class="btn-outline small"
                :key="social.avatar_url"
                :href="social.link"
                :title="`${social.platform?.name} - ${social.username}`"
              >
                <i :class="social.platform?.icon" class="flex h-4 w-4 items-center justify-center" />
              </Button>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </div>
    <div class="mt-10 block w-fit self-center md:hidden">
      <Button :to="{ name: 'about' }" class="btn-outline"> go to about </Button>
    </div>
  </section>
</template>

<style scoped>
.video-no-controls {
  /* Скрываем контролы видео на всех браузерах */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.video-no-controls::-webkit-media-controls {
  display: none !important;
}

.video-no-controls::-webkit-media-controls-panel {
  display: none !important;
}

.video-no-controls::-webkit-media-controls-play-button {
  display: none !important;
}

.video-no-controls::-webkit-media-controls-start-playback-button {
  display: none !important;
}

.video-no-controls::-moz-media-controls {
  display: none !important;
}

/* Дополнительно для Safari на iOS */
.video-no-controls::-webkit-media-controls-overlay-play-button {
  display: none !important;
}

.video-no-controls::-webkit-media-controls-fullscreen-button {
  display: none !important;
}
</style>
